import { Button, TextButton } from '@wix/design-system'
import { useContent, useNext, usePageLoading } from '@wix/funnel-builder-lizard'
import classNames from 'classnames'
import React, { FC, useCallback, useEffect, useState } from 'react'
import { useSearchParams } from 'react-router-dom'

import { markAsWixStudioAccount } from '../../../api/StudioWorkspaceService/markAsWixStudioAccount'
import { unmarkAsWixStudioAccount } from '../../../api/StudioWorkspaceService/unmarkAsWixStudioAccount'
import { usePageLogger } from '../../../bi/mfi'
import ArrowLeft from '../../reusable/icons/ArrowLeft.svg'
import WixStudioLogo from '../../reusable/icons/WixStudioLogo.svg'
import { RichText } from '../../reusable/RichText'
import Icon from './icon.svg'
import type { TWixStudioSplit as Content } from './WixStudioSplitV3.content'
import { wixStudioSplitV3DataHooks as dataHooks } from './WixStudioSplitV3.dataHooks'
import s from './WixStudioSplitV3.scss'

export const WixStudioSplitV3: FC = () => {
  const [searchParams] = useSearchParams()
  const [isPageLoading] = usePageLoading()
  const next = useNext()
  const { content } = useContent<Content>()
  const { question, secondaryQuestion } = content
  const pageLogger = usePageLogger()
  const [isAnimationStarted, setIsAnimationStarted] = useState<boolean>(false)

  const authMethod = searchParams.get('authMethod')
  const isDisclaimerVisible = content.disclaimer && authMethod === 'GOOGLE_ONE_TAP'

  useEffect(() => {
    void pageLogger.screenView()
  }, [pageLogger])

  const handleClick =
    (questionKey: string, optionKey: string, markAsStudio: boolean, unmarkAsStudio: boolean) => () => {
      if (isPageLoading) {
        return
      }
      if (markAsStudio) {
        void markAsWixStudioAccount()
      } else if (unmarkAsStudio) {
        void unmarkAsWixStudioAccount()
      }

      void pageLogger.answer({ answer: optionKey })
      next({
        [questionKey]: { values: [optionKey] },
      })
    }

  const handleAnimationStart =
    (questionKey: string, optionKey: string, markAsStudio: boolean, unmarkAsStudio: boolean) => () => {
      if (!isAnimationStarted && optionKey === 'setup') {
        setIsAnimationStarted(true)
        setTimeout(handleClick(questionKey, optionKey, markAsStudio, unmarkAsStudio), 300)
      } else {
        return handleClick(questionKey, optionKey, markAsStudio, unmarkAsStudio)()
      }
    }

  const secondaryOption = secondaryQuestion?.options[0]

  const handleSkipClick = useCallback(() => {
    if (isPageLoading) {
      return
    }
    void pageLogger.skip()
    next({
      [question.questionKey]: { skipped: true },
    })
  }, [isPageLoading, pageLogger, next, question.questionKey])

  const handleBackClick = useCallback(() => {
    if (isPageLoading) {
      return
    }
    void pageLogger.back()

    window.history.back()
  }, [isPageLoading, pageLogger])

  return (
    <div className={s.WixStudioSplitV3}>
      <div className={s.WixStudioSplitV3__header}>
        <WixStudioLogo className={s.WixStudioSplitV3__logo} />
        {content.backButton && (
          <>
            <hr className={s.WixStudioSplitV3__divider} />
            <TextButton
              className={s.WixStudioSplitV3__backButton}
              size="small"
              prefixIcon={<ArrowLeft className={s.WixStudioSplitV3__backIcon} />}
              skin="dark"
              onClick={handleBackClick}
            >
              {content.backButton}
            </TextButton>
          </>
        )}
        {content.skipButton && (
          <TextButton
            className={s.WixStudioSplitV3__skipButton}
            size="small"
            skin="dark"
            onClick={handleSkipClick}
            dataHook={dataHooks.skip()}
          >
            {content.skipButton}
          </TextButton>
        )}
      </div>
      <div
        className={classNames(s.WixStudioSplitV3__content, {
          [s.WixStudioSplitV3__contentFadeOut]: isAnimationStarted,
        })}
      >
        <div className={s.WixStudioSplitV3__welcomeLine} data-hook={dataHooks.title()}>
          {content.question.content.title}
        </div>
        <div className={s.WixStudioSplitV3__questionLine}>{content.question.content.subtitle}</div>
        <div className={s.WixStudioSplitV3__options}>
          {question.options.map(option => (
            <div
              data-hook={dataHooks.option({ key: option.optionKey })}
              key={option.optionKey}
              className={s.WixStudioSplitV3__option}
              style={{ backgroundColor: option.backgroundColor }}
              onClick={handleAnimationStart(
                question.questionKey,
                option.optionKey,
                option.markAsStudio,
                option.unmarkAsStudio,
              )}
            >
              <img className={s.WixStudioSplitV3__graphics} src={option.img.content.src} alt={option.img.content.alt} />
              <div className={s.WixStudioSplitV3__caption}>
                <div className={s.WixStudioSplitV3__optionTitle}>{option.label}</div>
                <div className={s.WixStudioSplitV3__optionSubtitle}>{option.description}</div>
                <Button className={s.WixStudioSplitV3__button}>
                  <span>{option.btn}</span>
                  <Icon />
                </Button>
              </div>
            </div>
          ))}
        </div>
        {secondaryOption && (
          <div className={s.WixStudioSplitV3__secondaryLinkContainer}>
            <span
              data-hook={dataHooks.secondaryOption({ key: secondaryOption.optionKey })}
              onClick={handleAnimationStart(
                secondaryQuestion.questionKey,
                secondaryOption.optionKey,
                secondaryOption.markAsStudio,
                secondaryOption.unmarkAsStudio,
              )}
              className={s.WixStudioSplitV3__secondaryLink}
            >
              <span>{secondaryOption.label}</span>
              <Icon />
            </span>
          </div>
        )}
      </div>
      {isDisclaimerVisible && (
        <div className={s.WixStudioSplitV3__footer}>
          <RichText className={s.WixStudioSplitV3__disclaimer} dataHook={dataHooks.disclaimer()}>
            {content.disclaimer}
          </RichText>
        </div>
      )}
    </div>
  )
}
